import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Sync as SyncIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesAPI, customUsersAPI, portalsAPI, usersAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

// Helper component to render permissions in nested format (read-only)
const NestedPermissionDisplay = ({ permissionsData }) => {
  if (!permissionsData || !permissionsData.modules || permissionsData.modules.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No permissions assigned
      </Typography>
    );
  }

  const { modules, permissions } = permissionsData;

  return (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      {modules.map((module) => (
        <Accordion key={module} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {module.charAt(0).toUpperCase() + module.slice(1)}
              </Typography>
              <Chip
                label={`${permissions[module]?.length || 0} features`}
                size="small"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Features */}
            {Array.isArray(permissions[module]) && permissions[module].map((featureObj, featureIndex) =>
              Object.entries(featureObj).map(([feature, subFeatures]) => (
                <Accordion key={`${feature}-${featureIndex}`} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <DescriptionIcon sx={{ mr: 2, color: 'secondary.main' }} />
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {feature.charAt(0).toUpperCase() + feature.slice(1).replace(/-/g, ' ')}
                      </Typography>
                      <Chip
                        label={`${subFeatures?.length || 0} sub-features`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Sub-features */}
                    {Array.isArray(subFeatures) && subFeatures.map((subFeatureObj, subFeatureIndex) =>
                      Object.entries(subFeatureObj).map(([subFeature, sections]) => (
                        <Box key={`${subFeature}-${subFeatureIndex}`} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SecurityIcon sx={{ mr: 2, color: 'info.main' }} />
                            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                              {subFeature.charAt(0).toUpperCase() + subFeature.slice(1).replace(/-/g, ' ')}
                            </Typography>
                          </Box>
                          {/* Sections */}
                          <Box sx={{ ml: 4 }}>
                            {Array.isArray(sections) && sections.map((section, sectionIndex) => (
                              <Box key={sectionIndex} sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <SecurityIcon sx={{ mr: 1, fontSize: 16, color: 'success.main' }} />
                                <Typography variant="body2">
                                  {section.charAt(0).toUpperCase() + section.slice(1).replace(/-/g, ' ')}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const UserManagement = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState(''); // individual/corporate for member type
  const [corporateFilter, setCorporateFilter] = useState(''); // corporate name for corporate members
  const [intermediaryFilter, setIntermediaryFilter] = useState(''); // intermediary name
  const [insurerFilter, setInsurerFilter] = useState(''); // insurer name
  const [providerFilter, setProviderFilter] = useState(''); // provider name
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkRoleDialogOpen, setBulkRoleDialogOpen] = useState(false);
  const [bulkRoleFormData, setBulkRoleFormData] = useState({
    role: '',
    action: 'assign' // 'assign', 'remove', 'replace'
  });
  const [createFormData, setCreateFormData] = useState({
    eoxegenId: '',
    name: '',
    userType: '',
    roles: [],
    // Conditional fields based on user type
    corporateId: '',
    corporateName: '',
    intermediaryId: '',
    intermediaryName: '',
    insurerId: '',
    insurerName: '',
    providerId: '',
    providerName: '',
    memberType: '', // 'individual' or 'corporate' for member type
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    userType: '',
    roles: [],
    status: '',
    // Conditional fields based on user type
    corporateId: '',
    corporateName: '',
    intermediaryId: '',
    intermediaryName: '',
    insurerId: '',
    insurerName: '',
    providerId: '',
    providerName: '',
    memberType: '', // 'individual' or 'corporate' for member type
  });

  const queryClient = useQueryClient();

  // Fetch custom users
  const { data: usersData, isLoading, error, refetch } = useQuery({
    queryKey: ['customUsers', { page: page + 1, limit: rowsPerPage, search, userType: userTypeFilter }],
    queryFn: () => customUsersAPI.getCustomUsers({
      page: page + 1,
      limit: rowsPerPage,
      search,
      userType: userTypeFilter
    }),
  });

  //Fetch all roles
  const {data: rolesData, isLoading:rolesLoading, error:rolesError, refetch:rolesRefetch} = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesAPI.getRoles()
  })

  // Fetch all portals
  const { data: portalsData } = useQuery({
    queryKey: ['portals'],
    queryFn: () => portalsAPI.getAllPortals()
  });

  console.log(rolesData?.data?.data)

  // Sync users mutation
  const syncUsersMutation = useMutation({
    mutationFn: usersAPI.syncUsers,
    onSuccess: (data) => {
      toast.success(`Sync completed: ${data.data.stats.synced} new, ${data.data.stats.updated} updated`);
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(`Sync failed: ${error.response?.data?.message || error.message}`);
    }
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: customUsersAPI.createCustomUser,
    onSuccess: (data) => {
      toast.success('User created successfully!');
      queryClient.invalidateQueries(['customUsers']);
      setCreateDialogOpen(false);
      setCreateFormData({
        eoxegenId: '',
        name: '',
        userType: '',
        roles: [],
        corporateId: '',
        corporateName: '',
        intermediaryId: '',
        intermediaryName: '',
        insurerId: '',
        insurerName: '',
        providerId: '',
        providerName: '',
        memberType: '',
      });
    },
    onError: (error) => {
      toast.error(`Failed to create user: ${error.response?.data?.message || error.message}`);
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }) => customUsersAPI.updateCustomUser(id, userData),
    onSuccess: (data) => {
      toast.success('User updated successfully!');
      queryClient.invalidateQueries(['customUsers']);
      setEditDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (error) => {
      toast.error(`Failed to update user: ${error.response?.data?.message || error.message}`);
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: customUsersAPI.deleteCustomUser,
    onSuccess: (data) => {
      toast.success('User deleted successfully!');
      queryClient.invalidateQueries(['customUsers']);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete user: ${error.response?.data?.message || error.message}`);
    }
  });

  // Bulk role assignment mutation
  const bulkAssignRolesMutation = useMutation({
    mutationFn: customUsersAPI.bulkAssignRoles,
    onSuccess: (data) => {
      const { processed, errors } = data.data.data;
      if (errors > 0) {
        toast.success(`Bulk role assignment completed: ${processed} successful, ${errors} failed`);
      } else {
        toast.success(`Bulk role assignment completed successfully for ${processed} users`);
      }
      queryClient.invalidateQueries(['customUsers']);
      setBulkRoleDialogOpen(false);
      setSelectedUsers([]);
      setBulkRoleFormData({ roles: [], action: 'assign' });
    },
    onError: (error) => {
      toast.error(`Failed to assign roles: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // Handle cascading filter changes
  const handleUserTypeFilterChange = (value) => {
    setUserTypeFilter(value);
    // Reset dependent filters
    setMemberTypeFilter('');
    setCorporateFilter('');
    setIntermediaryFilter('');
    setInsurerFilter('');
    setProviderFilter('');
    // Reset selections when filter changes
    setSelectedUsers([]);
    setPage(0);
  };

  const handleMemberTypeFilterChange = (value) => {
    setMemberTypeFilter(value);
    // Reset dependent filters
    setCorporateFilter('');
    setSelectedUsers([]);
    setPage(0);
  };

  const handleCorporateFilterChange = (value) => {
    setCorporateFilter(value);
    setSelectedUsers([]);
    setPage(0);
  };

  const handleIntermediaryFilterChange = (value) => {
    setIntermediaryFilter(value);
    setSelectedUsers([]);
    setPage(0);
  };

  const handleInsurerFilterChange = (value) => {
    setInsurerFilter(value);
    setSelectedUsers([]);
    setPage(0);
  };

  const handleProviderFilterChange = (value) => {
    setProviderFilter(value);
    setSelectedUsers([]);
    setPage(0);
  };





  const handleSyncUsers = () => {
    syncUsersMutation.mutate();
  };

  const handleCreateUser = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateFormData({
      eoxegenId: '',
      name: '',
      userType: '',
      roles: [],
      corporateId: '',
      corporateName: '',
      intermediaryId: '',
      intermediaryName: '',
      insurerId: '',
      insurerName: '',
      providerId: '',
      providerName: '',
      memberType: '',
    });
  };

  const handleCreateFormChange = (field, value) => {
    setCreateFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Clear conditional fields when user type changes
      if (field === 'userType') {
        newData.corporateId = '';
        newData.corporateName = '';
        newData.intermediaryId = '';
        newData.intermediaryName = '';
        newData.insurerId = '';
        newData.insurerName = '';
        newData.providerId = '';
        newData.providerName = '';
        newData.memberType = '';
      }

      // Clear corporate fields when member type changes from corporate to individual
      if (field === 'memberType' && value === 'individual') {
        newData.corporateId = '';
        newData.corporateName = '';
      }

      return newData;
    });
  };

  const handleSubmitCreateUser = () => {
    if (!createFormData.eoxegenId || !createFormData.name || !createFormData.userType) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!createFormData.roles || createFormData.roles.length === 0) {
      toast.error('Please select a role');
      return;
    }

    // Validate conditional fields based on user type
    const { userType, memberType } = createFormData;

    if (userType === 'corporate' && (!createFormData.corporateId || !createFormData.corporateName)) {
      toast.error('Please fill in Corporate ID and Corporate Name');
      return;
    }

    if (userType === 'intermediary' && (!createFormData.intermediaryId || !createFormData.intermediaryName)) {
      toast.error('Please fill in Intermediary ID and Intermediary Name');
      return;
    }

    if (userType === 'insurer' && (!createFormData.insurerId || !createFormData.insurerName)) {
      toast.error('Please fill in Insurer ID and Insurer Name');
      return;
    }

    if (userType === 'provider' && (!createFormData.providerId || !createFormData.providerName)) {
      toast.error('Please fill in Provider ID and Provider Name');
      return;
    }

    if (userType === 'member') {
      if (!memberType) {
        toast.error('Please select Member Type (Individual or Corporate)');
        return;
      }
      if (memberType === 'corporate' && (!createFormData.corporateId || !createFormData.corporateName)) {
        toast.error('Please fill in Corporate ID and Corporate Name for corporate member');
        return;
      }
    }

    createUserMutation.mutate(createFormData);
  };

  // Get roles filtered by selected portal type
  const getFilteredRoles = (userType = null) => {
    const targetUserType = userType || createFormData.userType;
    if (!targetUserType || !rolesData?.data?.data) {
      return [];
    }
    return rolesData.data.data.filter(role =>
      role.portalAccess && role.portalAccess.includes(targetUserType)
    );
  };

  // Get roles for bulk assignment - only roles matching the filtered user type
  const getBulkAssignmentRoles = () => {
    if (!userTypeFilter || !rolesData?.data?.data) return [];
    return rolesData.data.data.filter(role =>
      role.portalAccess && role.portalAccess.includes(userTypeFilter)
    );
  };



  // Get corporate names - for corporate user type OR corporate members
  const getCorporateNames = () => {
    if (!allUsers) return [];
    return allUsers
      .filter(user =>
        (user.userType === 'corporate' && user.corporateName) ||
        (user.userType === 'member' && user.memberType === 'corporate' && user.corporateName)
      )
      .map(user => user.corporateName)
      .filter((name, index, self) => self.indexOf(name) === index)
      .sort();
  };

  // Get provider names
  const getProviderNames = () => {
    if (!allUsers) return [];
    return allUsers
      .filter(user => user.userType === 'provider' && user.providerName)
      .map(user => user.providerName)
      .filter((name, index, self) => self.indexOf(name) === index)
      .sort();
  };

  // Get intermediary names
  const getIntermediaryNames = () => {
    if (!allUsers) return [];
    return allUsers
      .filter(user => user.userType === 'intermediary' && user.intermediaryName)
      .map(user => user.intermediaryName)
      .filter((name, index, self) => self.indexOf(name) === index)
      .sort();
  };

  // Get insurer names
  const getInsurerNames = () => {
    if (!allUsers) return [];
    return allUsers
      .filter(user => user.userType === 'insurer' && user.insurerName)
      .map(user => user.insurerName)
      .filter((name, index, self) => self.indexOf(name) === index)
      .sort();
  };

  // View user handler
  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setSelectedUserPermissions(null);
    setViewDialogOpen(true);

    // Fetch user permissions
    try {
      const { eoxegenAuthAPI } = await import('../../shared/services/api');
      const permissionsResponse = await eoxegenAuthAPI.getPermissionsByEoxegenId(user.eoxegenId);
      setSelectedUserPermissions(permissionsResponse.data);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      toast.error('Failed to load user permissions');
    }
  };

  // Edit user handler
  const handleEditUserAction = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      userType: user.userType,
      roles: user.roles?.length > 0 ? [user.roles[0]._id] : [],
      status: user.status,
      // Load existing conditional fields if they exist
      corporateId: user.corporateId || '',
      corporateName: user.corporateName || '',
      intermediaryId: user.intermediaryId || '',
      intermediaryName: user.intermediaryName || '',
      insurerId: user.insurerId || '',
      insurerName: user.insurerName || '',
      providerId: user.providerId || '',
      providerName: user.providerName || '',
      memberType: user.memberType || '',
    });
    setEditDialogOpen(true);
  };

  // Delete user handler
  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Close dialogs handlers
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedUser(null);
    setSelectedUserPermissions(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditFormData({
      name: '',
      userType: '',
      roles: [],
      status: '',
      corporateId: '',
      corporateName: '',
      intermediaryId: '',
      intermediaryName: '',
      insurerId: '',
      insurerName: '',
      providerId: '',
      providerName: '',
      memberType: '',
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // Edit form change handler
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Clear conditional fields when user type changes
      if (field === 'userType') {
        newData.corporateId = '';
        newData.corporateName = '';
        newData.intermediaryId = '';
        newData.intermediaryName = '';
        newData.insurerId = '';
        newData.insurerName = '';
        newData.providerId = '';
        newData.providerName = '';
        newData.memberType = '';
      }

      // Clear corporate fields when member type changes from corporate to individual
      if (field === 'memberType' && value === 'individual') {
        newData.corporateId = '';
        newData.corporateName = '';
      }

      return newData;
    });
  };

  // Submit edit user
  const handleSubmitEditUser = () => {
    if (!editFormData.name || !editFormData.userType) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editFormData.roles || editFormData.roles.length === 0) {
      toast.error('Please select a role');
      return;
    }

    // Validate conditional fields based on user type
    const { userType, memberType } = editFormData;

    if (userType === 'corporate' && (!editFormData.corporateId || !editFormData.corporateName)) {
      toast.error('Please fill in Corporate ID and Corporate Name');
      return;
    }

    if (userType === 'intermediary' && (!editFormData.intermediaryId || !editFormData.intermediaryName)) {
      toast.error('Please fill in Intermediary ID and Intermediary Name');
      return;
    }

    if (userType === 'insurer' && (!editFormData.insurerId || !editFormData.insurerName)) {
      toast.error('Please fill in Insurer ID and Insurer Name');
      return;
    }

    if (userType === 'provider' && (!editFormData.providerId || !editFormData.providerName)) {
      toast.error('Please fill in Provider ID and Provider Name');
      return;
    }

    if (userType === 'member') {
      if (!memberType) {
        toast.error('Please select Member Type (Individual or Corporate)');
        return;
      }
      if (memberType === 'corporate' && (!editFormData.corporateId || !editFormData.corporateName)) {
        toast.error('Please fill in Corporate ID and Corporate Name for corporate member');
        return;
      }
    }

    updateUserMutation.mutate({
      id: selectedUser._id,
      userData: editFormData
    });
  };

  // Submit delete user
  const handleSubmitDeleteUser = () => {
    deleteUserMutation.mutate(selectedUser._id);
  };

  // Bulk selection handlers - only allow selection when userType filter is applied
  const handleSelectUser = (userId) => {
    if (!userTypeFilter) {
      toast.error('Please select a user type filter first to enable bulk selection');
      return;
    }

    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAllUsers = (checked) => {
    if (!userTypeFilter) {
      toast.error('Please select a user type filter first to enable bulk selection');
      return;
    }

    if (checked) {
      // Select all filtered users
      const filteredUserIds = users.map(user => user._id);
      setSelectedUsers(filteredUserIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleBulkRoleAssignment = () => {
    if (!userTypeFilter) {
      toast.error('Please select a user type filter first');
      return;
    }
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    setBulkRoleDialogOpen(true);
  };

  const handleCloseBulkRoleDialog = () => {
    setBulkRoleDialogOpen(false);
    setBulkRoleFormData({ role: '', action: 'assign' });
  };

  const handleBulkRoleFormChange = (field, value) => {
    setBulkRoleFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitBulkRoleAssignment = () => {
    if (!bulkRoleFormData.role) {
      toast.error('Please select a role');
      return;
    }

    bulkAssignRolesMutation.mutate({
      userIds: selectedUsers,
      roleIds: [bulkRoleFormData.role], // Convert single role to array for API
      action: bulkRoleFormData.action
    });
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      admin: 'error',
      member: 'primary',
      corporate: 'success',
      provider: 'warning',
      intermediary: 'info',
      insurer: 'secondary',
      irda: 'default'
    };
    return colors[userType] || 'default';
  };

  // Helper function to render conditional fields based on user type
  const renderConditionalFields = (formData, handleFormChange, isEdit = false) => {
    const { userType, memberType } = formData;

    switch (userType) {
      case 'corporate':
        return (
          <>
            <TextField
              label="Corporate ID"
              value={formData.corporateId}
              onChange={(e) => handleFormChange('corporateId', e.target.value)}
              fullWidth
              required
              helperText="Unique corporate identifier"
            />
            <TextField
              label="Corporate Name"
              value={formData.corporateName}
              onChange={(e) => handleFormChange('corporateName', e.target.value)}
              fullWidth
              required
              helperText="Name of the corporate entity"
            />
          </>
        );

      case 'intermediary':
        return (
          <>
            <TextField
              label="Intermediary ID"
              value={formData.intermediaryId}
              onChange={(e) => handleFormChange('intermediaryId', e.target.value)}
              fullWidth
              required
              helperText="Unique intermediary identifier"
            />
            <TextField
              label="Intermediary Name"
              value={formData.intermediaryName}
              onChange={(e) => handleFormChange('intermediaryName', e.target.value)}
              fullWidth
              required
              helperText="Name of the intermediary"
            />
          </>
        );

      case 'insurer':
        return (
          <>
            <TextField
              label="Insurer ID"
              value={formData.insurerId}
              onChange={(e) => handleFormChange('insurerId', e.target.value)}
              fullWidth
              required
              helperText="Unique insurer identifier"
            />
            <TextField
              label="Insurer Name"
              value={formData.insurerName}
              onChange={(e) => handleFormChange('insurerName', e.target.value)}
              fullWidth
              required
              helperText="Name of the insurance company"
            />
          </>
        );

      case 'provider':
        return (
          <>
            <TextField
              label="Provider ID"
              value={formData.providerId}
              onChange={(e) => handleFormChange('providerId', e.target.value)}
              fullWidth
              required
              helperText="Unique provider identifier"
            />
            <TextField
              label="Provider Name"
              value={formData.providerName}
              onChange={(e) => handleFormChange('providerName', e.target.value)}
              fullWidth
              required
              helperText="Name of the healthcare provider"
            />
          </>
        );

      case 'member':
        return (
          <>
            <FormControl fullWidth required>
              <InputLabel>Member Type</InputLabel>
              <Select
                value={memberType}
                label="Member Type"
                onChange={(e) => handleFormChange('memberType', e.target.value)}
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select whether this is an individual or corporate member
              </Typography>
            </FormControl>

            {memberType === 'corporate' && (
              <>
                <TextField
                  label="Corporate ID"
                  value={formData.corporateId}
                  onChange={(e) => handleFormChange('corporateId', e.target.value)}
                  fullWidth
                  required
                  helperText="Corporate identifier for corporate member"
                />
                <TextField
                  label="Corporate Name"
                  value={formData.corporateName}
                  onChange={(e) => handleFormChange('corporateName', e.target.value)}
                  fullWidth
                  required
                  helperText="Name of the corporate entity"
                />
              </>
            )}
          </>
        );

      case 'irda':
        // No additional fields for IRDA
        return null;

      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      suspended: 'error'
    };
    return colors[status] || 'default';
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load users: {error.response?.data?.message || error.message}
      </Alert>
    );
  }

  const allUsers = usersData?.data?.data || [];
  const pagination = usersData?.data?.pagination || {};

  // Apply cascading filters
  const users = allUsers.filter(user => {
    // Search filter
    if (search && !user.name.toLowerCase().includes(search.toLowerCase()) &&
        !user.email?.toLowerCase().includes(search.toLowerCase()) &&
        !user.eoxegenId?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }

    // User type filter
    if (userTypeFilter && user.userType !== userTypeFilter) {
      return false;
    }

    // Member type filter (for member users)
    if (userTypeFilter === 'member' && memberTypeFilter && user.memberType !== memberTypeFilter) {
      return false;
    }

    // Corporate filter (for corporate user type OR corporate members)
    if (corporateFilter) {
      if (userTypeFilter === 'corporate' && user.corporateName !== corporateFilter) {
        return false;
      }
      if (userTypeFilter === 'member' && memberTypeFilter === 'corporate' && user.corporateName !== corporateFilter) {
        return false;
      }
    }

    // Intermediary filter
    if (userTypeFilter === 'intermediary' && intermediaryFilter && user.intermediaryName !== intermediaryFilter) {
      return false;
    }

    // Insurer filter
    if (userTypeFilter === 'insurer' && insurerFilter && user.insurerName !== insurerFilter) {
      return false;
    }

    // Provider filter
    if (userTypeFilter === 'provider' && providerFilter && user.providerName !== providerFilter) {
      return false;
    }

    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
            disabled={isLoading}
          >
            Create User
          </Button>
          {selectedUsers.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleBulkRoleAssignment}
              disabled={bulkAssignRolesMutation.isPending || !userTypeFilter || selectedUsers.length === 0}
              title={!userTypeFilter ? "Select a user type filter first" : selectedUsers.length === 0 ? "Select users to assign roles" : "Assign roles to selected users"}
            >
              Assign Roles ({selectedUsers.length})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncUsers}
            disabled={syncUsersMutation.isPending}
          >
            {syncUsersMutation.isPending ? 'Syncing...' : 'Sync from eOxegen'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search users..."
            value={search}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>User Type</InputLabel>
            <Select
              value={userTypeFilter}
              label="User Type"
              onChange={(e) => handleUserTypeFilterChange(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="corporate">Corporate</MenuItem>
              <MenuItem value="provider">Provider</MenuItem>
              <MenuItem value="intermediary">Intermediary</MenuItem>
              <MenuItem value="insurer">Insurer</MenuItem>
              <MenuItem value="irda">IRDA</MenuItem>
            </Select>
          </FormControl>

          {/* Corporate Name Filter - only show for corporate user type */}
          {userTypeFilter === 'corporate' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Corporate Name</InputLabel>
              <Select
                value={corporateFilter}
                label="Corporate Name"
                onChange={(e) => handleCorporateFilterChange(e.target.value)}
              >
                <MenuItem value="">All Corporates</MenuItem>
                {getCorporateNames().map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Member Type Filter - only show for member user type */}
          {userTypeFilter === 'member' && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Member Type</InputLabel>
              <Select
                value={memberTypeFilter}
                label="Member Type"
                onChange={(e) => handleMemberTypeFilterChange(e.target.value)}
              >
                <MenuItem value="">All Member Types</MenuItem>
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Corporate Name Filter - only show for corporate members */}
          {userTypeFilter === 'member' && memberTypeFilter === 'corporate' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Corporate Name</InputLabel>
              <Select
                value={corporateFilter}
                label="Corporate Name"
                onChange={(e) => handleCorporateFilterChange(e.target.value)}
              >
                <MenuItem value="">All Corporates</MenuItem>
                {getCorporateNames().map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Intermediary Name Filter - only show for intermediary user type */}
          {userTypeFilter === 'intermediary' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Intermediary Name</InputLabel>
              <Select
                value={intermediaryFilter}
                label="Intermediary Name"
                onChange={(e) => handleIntermediaryFilterChange(e.target.value)}
              >
                <MenuItem value="">All Intermediaries</MenuItem>
                {getIntermediaryNames().map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Insurer Name Filter - only show for insurer user type */}
          {userTypeFilter === 'insurer' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Insurer Name</InputLabel>
              <Select
                value={insurerFilter}
                label="Insurer Name"
                onChange={(e) => handleInsurerFilterChange(e.target.value)}
              >
                <MenuItem value="">All Insurers</MenuItem>
                {getInsurerNames().map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Provider Name Filter - only show for provider user type */}
          {userTypeFilter === 'provider' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Provider Name</InputLabel>
              <Select
                value={providerFilter}
                label="Provider Name"
                onChange={(e) => handleProviderFilterChange(e.target.value)}
              >
                <MenuItem value="">All Providers</MenuItem>
                {getProviderNames().map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* <Tooltip title="Filter options">
            <IconButton>
              <FilterIcon />
            </IconButton>
          </Tooltip> */}
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper sx={{width:"100%"}}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {userTypeFilter && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      checked={users.length > 0 && selectedUsers.length === users.length}
                      onChange={(e) => handleSelectAllUsers(e.target.checked)}
                      title="Select all filtered users"
                    />
                  </TableCell>
                )}
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>UID</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={userTypeFilter ? 10 : 9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userTypeFilter ? 10 : 9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    {userTypeFilter && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          title="Select user"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {user.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.email || `${user.eoxegenId}@hitpa.local`}</TableCell>
                    <TableCell>{user.username || `user_${user.eoxegenId}`}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.userType}
                        size="small"
                        color={getUserTypeColor(user.userType)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        size="small"
                        color={getStatusColor(user.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.eoxegenId || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.roles.length > 0 ? (
                        <Chip
                          label={user.roles[0].name}
                          size="small"
                          variant="outlined"
                        />
                      ):(
                        <Typography variant="body2" color="text.secondary">
                          No roles assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View User">
                          <IconButton
                            size="small"
                            onClick={() => handleViewUser(user)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleEditUserAction(user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit User Dialog - Removed duplicate, using the complete one below */}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* eOxegen ID Field */}
            <TextField
              label="eOxegen UID"
              value={createFormData.eoxegenId}
              onChange={(e) => handleCreateFormChange('eoxegenId', e.target.value)}
              fullWidth
              required
              helperText="Unique identifier from eOxegen system"
            />

            {/* Name Field */}
            <TextField
              label="Full Name"
              value={createFormData.name}
              onChange={(e) => handleCreateFormChange('name', e.target.value)}
              fullWidth
              required
              helperText="User's full name"
            />

            {/* User Type/Portal Dropdown */}
            <FormControl fullWidth required>
              <InputLabel>User Type / Portal</InputLabel>
              <Select
                value={createFormData.userType}
                label="User Type / Portal"
                onChange={(e) => {
                  handleCreateFormChange('userType', e.target.value);
                  handleCreateFormChange('roles', []); // Reset roles when portal changes
                }}
              >
                {portalsData?.data?.data?.map((portal) => (
                  <MenuItem key={portal._id} value={portal.type}>
                    {portal.name} ({portal.type})
                  </MenuItem>
                )) || [
                  <MenuItem key="admin" value="admin">Admin Portal</MenuItem>,
                  <MenuItem key="member" value="member">Member Portal</MenuItem>,
                  <MenuItem key="corporate" value="corporate">Corporate Portal</MenuItem>,
                  <MenuItem key="provider" value="provider">Provider Portal</MenuItem>,
                  <MenuItem key="intermediary" value="intermediary">Intermediary Portal</MenuItem>,
                  <MenuItem key="insurer" value="insurer">Insurer Portal</MenuItem>,
                  <MenuItem key="irda" value="irda">IRDA Portal</MenuItem>
                ]}
              </Select>
            </FormControl>

            {/* Conditional Fields Based on User Type */}
            {createFormData.userType && renderConditionalFields(createFormData, handleCreateFormChange)}

            {/* Roles Dropdown - Only show if portal is selected */}
            {createFormData.userType && (
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={createFormData.roles[0] || ''}
                  label="Role"
                  onChange={(e) => handleCreateFormChange('roles', [e.target.value])}
                >
                  {getFilteredRoles().map((role) => (
                    <MenuItem key={role._id} value={role._id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Select a role for {createFormData.userType} portal
                </Typography>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitCreateUser}
            disabled={createUserMutation.isPending}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedUser.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  eOxegen UID: {selectedUser.eoxegenId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Email: {selectedUser.email || `${selectedUser.eoxegenId}@hitpa.local`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Username: {selectedUser.username || `user_${selectedUser.eoxegenId}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Chip
                    label={selectedUser.userType}
                    size="small"
                    color={getUserTypeColor(selectedUser.userType)}
                    variant="outlined"
                  />
                  <Chip
                    label={selectedUser.status}
                    size="small"
                    color={getStatusColor(selectedUser.status)}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* User Roles */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Assigned Role
                </Typography>
                {selectedUserPermissions?.data?.user?.roles && selectedUserPermissions.data.user.roles.length > 0 ? (
                  <Chip
                    label={selectedUserPermissions.data.user.roles[0]}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ) : selectedUser.roles && selectedUser.roles.length > 0 ? (
                  <Chip
                    label={selectedUser.roles[0].name}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No role assigned
                  </Typography>
                )}
              </Box>

              {/* User Permissions */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                {selectedUserPermissions ? (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Modules: {selectedUserPermissions.data?.modules?.length || 0} |
                      Total Sections: {selectedUserPermissions.data?.permissionCount || 0}
                    </Typography>
                    <NestedPermissionDisplay
                      permissionsData={selectedUserPermissions.data}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Loading permissions...
                  </Typography>
                )}
              </Box>

              {/* Audit Information */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Audit Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Created: {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Last Updated: {selectedUser.updatedAt ? new Date(selectedUser.updatedAt).toLocaleString() : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Login: {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString() : 'Never'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button variant="contained" onClick={() => {
            handleCloseViewDialog();
            handleEditUserAction(selectedUser);
          }}>
            Edit User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={editFormData.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                fullWidth
                required
              />

              <FormControl fullWidth required>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={editFormData.userType}
                  label="User Type"
                  onChange={(e) => {
                    handleEditFormChange('userType', e.target.value);
                    handleEditFormChange('roles', []); // Reset roles when portal changes
                  }}
                >
                  {portalsData?.data?.data?.map((portal) => (
                    <MenuItem key={portal._id} value={portal.type}>
                      {portal.name} ({portal.type})
                    </MenuItem>
                  )) || [
                    <MenuItem key="admin" value="admin">Admin Portal</MenuItem>,
                    <MenuItem key="member" value="member">Member Portal</MenuItem>,
                    <MenuItem key="corporate" value="corporate">Corporate Portal</MenuItem>,
                    <MenuItem key="provider" value="provider">Provider Portal</MenuItem>,
                    <MenuItem key="intermediary" value="intermediary">Intermediary Portal</MenuItem>,
                    <MenuItem key="insurer" value="insurer">Insurer Portal</MenuItem>,
                    <MenuItem key="irda" value="irda">IRDA Portal</MenuItem>
                  ]}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editFormData.status}
                  label="Status"
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>

              {/* Conditional Fields Based on User Type */}
              {editFormData.userType && renderConditionalFields(editFormData, handleEditFormChange, true)}

              {editFormData.userType && (
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editFormData.roles[0] || ''}
                    onChange={(e) => handleEditFormChange('roles', [e.target.value])}
                    label="Role"
                  >
                    {getFilteredRoles(editFormData.userType).map((role) => (
                      <MenuItem key={role._id} value={role._id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Select a role for {editFormData.userType} portal
                  </Typography>
                </FormControl>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitEditUser}
            disabled={updateUserMutation.isPending}
          >
            {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. The user will be permanently deleted from the system.
              </Alert>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete the following user?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Name: {selectedUser.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  eOxegen UID: {selectedUser.eoxegenId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User Type: {selectedUser.userType}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitDeleteUser}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Role Assignment Dialog */}
      <Dialog open={bulkRoleDialogOpen} onClose={handleCloseBulkRoleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Role Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Assign roles to {selectedUsers.length} selected user(s)
              </Typography>
              <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                User Type: {userTypeFilter?.charAt(0).toUpperCase() + userTypeFilter?.slice(1)} Portal
              </Typography>
            </Box>

            {/* Action Type */}
            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                value={bulkRoleFormData.action}
                label="Action"
                onChange={(e) => handleBulkRoleFormChange('action', e.target.value)}
              >
                <MenuItem value="assign">Assign Roles (Add to existing)</MenuItem>
                <MenuItem value="replace">Replace Roles (Replace all existing)</MenuItem>
                <MenuItem value="remove">Remove Roles (Remove from existing)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {bulkRoleFormData.action === 'assign' && 'Add selected roles to users\' existing roles'}
                {bulkRoleFormData.action === 'replace' && 'Replace all existing roles with selected roles'}
                {bulkRoleFormData.action === 'remove' && 'Remove selected roles from users\' existing roles'}
              </Typography>
            </FormControl>

            {/* Role Selection */}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={bulkRoleFormData.role}
                label="Role"
                onChange={(e) => handleBulkRoleFormChange('role', e.target.value)}
              >
                {getBulkAssignmentRoles().map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    <ListItemText primary={role.name} secondary={role.description} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select role to {bulkRoleFormData.action}
              </Typography>
            </FormControl>

            {/* Selected Users Preview */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Users ({selectedUsers.length}):
              </Typography>
              {/* <Box sx={{ maxHeight: 150, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                {users?.data?.filter(user => selectedUsers.includes(user._id)).map(user => (
                  <Typography key={user._id} variant="body2" sx={{ py: 0.5 }}>
                    • {user.name} ({user.userType})
                  </Typography>
                ))}
              </Box> */}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkRoleDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitBulkRoleAssignment}
            disabled={bulkAssignRolesMutation.isPending || !bulkRoleFormData.role}
          >
            {bulkAssignRolesMutation.isPending ? 'Processing...' : `${bulkRoleFormData.action.charAt(0).toUpperCase() + bulkRoleFormData.action.slice(1)} Roles`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
